import { ArrowDownTrayIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline'
import cloneDeep from 'clone-deep'
import { LayoutLoading } from 'components/LayoutLoading'
import { SendEmailDialog } from 'components/SendEmailDialog'
import { BaseFile, COMPANY_NAME_FC, InputType, servicingEmail } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'
import {
  downloadLoanIntakeDocs,
  getLoanIntakeDocumentData,
  getMortgageClauseTemplates,
  sendLoanIntakeDocumentEmail,
  updateLoanIntakeDocumentData,
} from 'services'
import Api from 'services/api'
import { openS3Document } from 'services/apis/documents'
import { svgLoading } from 'stories/assets'
import { PlainTable } from 'stories/components'
import { borrowerRecipients, emailDirDefaults, isEmpty } from 'utils'
import { emailObj2Txt } from 'utils/convertor'
import { RenderInput } from 'utils/RenderInput'

import { defaultInputs } from './constants'

interface ServicingDoc {
  name: string
  placeholders: string[]
  email: { subject: string; comment: string }
}

export default function LoanIntakeDocument() {
  const loanNumber = Api.getLoanNumber()
  const { borrower } = useSelector((state: any) => ({
    borrower: state.borrower.borrower,
  }))
  const [loading, setLoading] = useState(true)

  /**
   * Address the borrower at every address on file. The selectable rows come from the loan-parties
   * picker (`loadParties`), which offers all of them; these are the ones checked when the dialog
   * opens, so the additional addresses are not left for the sender to tick by hand.
   */
  const borrowerDefaults = useMemo(() => emailDirDefaults(borrowerRecipients(borrower)), [borrower])
  const [documents, setDocuments] = useState<ServicingDoc[]>([])
  const [histories, setHistories] = useState<any[]>([])
  const [placeholders, setPlaceholders] = useState<any>({})
  const [docRlt, setDocRlt] = useState<any>({})
  const [currentDocument, setCurrentDocument] = useState<ServicingDoc | null>(null)
  const [missingPlaceholders, setMissingPlaceholders] = useState<any>([])
  const [docData, setDocData] = useState<Record<string, any>>({})
  const [orgDocData, setOrgDocData] = useState<Record<string, any>>({})
  const [inputs, setInputs] = useState<Record<string, InputType>>(defaultInputs())

  const init = async () => {
    setLoading(true)

    const [res, templateRes] = await Promise.all([getLoanIntakeDocumentData(), getMortgageClauseTemplates()])

    if (res.success) {
      setDocuments(res.documents)
      setPlaceholders(res.placeholders)
      setDocRlt(res.docRlt)
      setHistories(res.histories)
      setDocData(res.data || {})
      setOrgDocData(res.data || {})
    }

    if (templateRes.success) {
      const mortgageClauseCplOptions: Record<string, any> = {}
      const mortgageClauseInsuranceOptions: Record<string, any> = {}

      templateRes.data.forEach((t: any) => {
        if (t.type === 'cpl') {
          mortgageClauseCplOptions[t.id] = t.title
        } else {
          mortgageClauseInsuranceOptions[t.id] = t.title
        }
      })

      const temp = cloneDeep(inputs)
      temp.mortgageClauseCpl.options = mortgageClauseCplOptions
      temp.mortgageClauseInsurance.options = mortgageClauseInsuranceOptions
      setInputs(temp)
    }

    setLoading(false)
  }

  useEffect(() => {
    init()
  }, [])

  const onChange = async (key: string, value: any) => {
    const temp = cloneDeep(docData)
    temp[key] = value
    setDocData(temp)
    if (inputs[key].inputType === 'radio') {
      await onBlur(key, value)
    }
  }

  const onBlur = async (key: string, _value?: any) => {
    const value = _value === undefined ? docData[key] : _value
    if (value === orgDocData[key]) return
    setLoading(true)
    await updateLoanIntakeDocumentData({ [key]: value })
    setLoading(false)
    setOrgDocData(docData)
  }

  const checkPlaceholders = (doc: ServicingDoc) => {
    let errors: any = []
    doc.placeholders.map((key) => {
      if (isEmpty(docRlt[key])) {
        errors.push(placeholders[key] || key)
      }
    })
    errors = errors.sort()
    setMissingPlaceholders(errors)
    return true
  }

  const onDownload = (doc: ServicingDoc) => {
    // if (!checkPlaceholders(doc)) return
    setLoading(true)
    downloadLoanIntakeDocs(doc.name).finally(() => setLoading(false))
  }

  const onSendToBorrower = (doc: ServicingDoc) => {
    if (!checkPlaceholders(doc)) return
    setCurrentDocument(doc)
  }

  // A send that fails still answers 200 with `success: false`, and the response carries `status`
  // rather than `message`, so the Api layer's own toast stays quiet. Reporting success on any
  // resolved response told staff the document had gone out when nothing had been delivered.
  const onSendEmail = (emailData: Record<string, string>, data: Record<string, any>) => {
    if (!currentDocument) return
    return sendLoanIntakeDocumentEmail(currentDocument.name, emailData, data).then((res: any) => {
      if (res?.success === false) {
        toast(res?.status || 'The document could not be emailed.', { type: 'error' })
        return
      }
      toast('Document is sent on email successfully.', { type: 'info' })
    })
  }

  return (
    <div className="servicing-document-container">
      <div className="relative max-w-screen-2xl">
        <LayoutLoading show={loading} />
        <h2 className="text-2xl flex items-center gap-1 font-bold mb-2">
          <span>Loan Intake Documents</span>
          {loading && <img src={svgLoading} className="inline w-6 h-6 text-white animate-spin ml-2" />}
        </h2>
        <div className="overflow-auto mb-6">
          <table className="text-[15px] w-full">
            <thead>
              <tr>
                <th className="border p-2">No</th>
                <th className="border p-2">Document Name</th>
                <th className="border p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((item, index) => {
                return (
                  <tr key={index} className={`${index % 2 ? 'bg-gray-50' : ''}`}>
                    <td className="border p-2">{index + 1}</td>
                    <td className="border p-2">{item.name}</td>
                    <td className="border px-2 py-3">
                      <div className="flex flex-wrap gap-4">
                        <div
                          className="flex items-center gap-1 cursor-pointer hover:underline hover:text-shade-blue"
                          onClick={() => onDownload(item)}
                        >
                          Download
                          <ArrowDownTrayIcon className="w-4 h-3"></ArrowDownTrayIcon>
                        </div>
                        <div
                          className="flex items-center gap-1 cursor-pointer hover:underline hover:text-shade-blue"
                          onClick={() => onSendToBorrower(item)}
                        >
                          Send to Borrower
                          <PaperAirplaneIcon className="w-4 h-3"></PaperAirplaneIcon>
                        </div>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        <div className="grid grid-cols-2 gap-4 mb-6">
          {Object.keys(inputs).map((key) => {
            const input = inputs[key]
            input.value = docData[key] || ''
            if (key === 'insuranceCompany') input.value = input.value || 'Hazard Insurance Company'
            return (
              <div className={`col-span-${input.span || 1}`} key={key}>
                <RenderInput
                  input={input}
                  Key={key}
                  onChange={(key: string, value: any) => onChange(key, value)}
                  onBlur={(key: string, value: any) => onBlur(key, value)}
                />
              </div>
            )
          })}
        </div>
        <div className="mb-4">
          <div className="border-b pl-2 pb-1">
            <div> History</div>
          </div>
          <PlainTable
            header={['No', 'Document', 'Emails', 'Attachments', 'Date', 'By']}
            tdClass="px-4 py-2"
            data={histories.map((item, index) => {
              const emailData = JSON.parse(item[2])
              const emailMap: Record<string, string> = {}
              ;(['to', 'cc', 'bcc'] as const).forEach((type) => {
                ;(emailData[type] || []).forEach((email: string) => {
                  emailMap[email] = type
                })
              })
              const emailText = emailObj2Txt(emailMap, '')

              const attachments = (
                <div className="flex flex-col gap-1">
                  {(emailData.files || []).map((file: BaseFile, fi: number) => (
                    <button
                      key={fi}
                      className="btn-link"
                      title={file.name}
                      onClick={() => openS3Document(file.fileKey, true)}
                    >
                      {file.name}
                    </button>
                  ))}
                </div>
              )

              return [index + 1, item[1], emailText, attachments, item[3], item[4]]
            })}
          />
        </div>
        {currentDocument && (
          <SendEmailDialog
            title={`Send "${currentDocument.name}" Document`}
            data={[{ party: `${COMPANY_NAME_FC} Servicing`, email: servicingEmail() }]}
            emailData={currentDocument.email}
            defaultValues={{
              ...borrowerDefaults,
              [servicingEmail()]: 'cc',
            }}
            hadFile
            filePath={`loan/${loanNumber}/intakeDocument`}
            onSubmit={onSendEmail}
            onClose={() => setCurrentDocument(null)}
            errors={missingPlaceholders}
            loadParties
            modalId="sendEmailLoanIntakeDocument"
          />
        )}
      </div>
    </div>
  )
}
